/**
 * Offline Storage Utility
 * Uses IndexedDB to store audio files and metadata for offline playback
 */

const DB_NAME = 'leden-offline';
const DB_VERSION = 2; // Incremented for peaks store
const AUDIO_STORE = 'audio-files';
const METADATA_STORE = 'metadata';
const PEAKS_STORE = 'waveform-peaks';

interface OfflineFile {
  id: string;
  songId: string;
  voicePartId?: string;
  blob: Blob;
  mimeType: string;
  size: number;
  downloadedAt: number;
}

interface OfflineMetadata {
  songId: string;
  title: string;
  artist?: string;
  coverUrl?: string;
  voicePartName?: string;
  fileId: string;
  voicePartId?: string;
  downloadedAt: number;
}

interface WaveformPeaks {
  fileId: string;
  peaks: number[][]; // Multi-channel peaks data
  duration: number;
  sampleRate: number;
  createdAt: number;
}

export interface StorageUsage {
  used: number;
  total: number;
  percentage: number;
}

// Initialize the database
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create audio files store
      if (!db.objectStoreNames.contains(AUDIO_STORE)) {
        const audioStore = db.createObjectStore(AUDIO_STORE, { keyPath: 'id' });
        audioStore.createIndex('songId', 'songId', { unique: false });
        audioStore.createIndex('voicePartId', 'voicePartId', { unique: false });
      }

      // Create metadata store
      if (!db.objectStoreNames.contains(METADATA_STORE)) {
        const metaStore = db.createObjectStore(METADATA_STORE, { keyPath: 'fileId' });
        metaStore.createIndex('songId', 'songId', { unique: false });
      }

      // Create peaks store (v2)
      if (!db.objectStoreNames.contains(PEAKS_STORE)) {
        db.createObjectStore(PEAKS_STORE, { keyPath: 'fileId' });
      }
    };
  });
}

/**
 * Save an audio file to offline storage
 */
export async function saveFile(
  fileId: string,
  songId: string,
  voicePartId: string | undefined,
  blob: Blob,
  metadata: Omit<OfflineMetadata, 'fileId' | 'downloadedAt'>
): Promise<void> {
  const db = await openDB();

  const offlineFile: OfflineFile = {
    id: fileId,
    songId,
    voicePartId,
    blob,
    mimeType: blob.type,
    size: blob.size,
    downloadedAt: Date.now(),
  };

  const offlineMetadata: OfflineMetadata = {
    ...metadata,
    fileId,
    downloadedAt: Date.now(),
  };

  // Save both in a transaction
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([AUDIO_STORE, METADATA_STORE], 'readwrite');
    transaction.onerror = () => reject(transaction.error);
    transaction.oncomplete = () => resolve();

    transaction.objectStore(AUDIO_STORE).put(offlineFile);
    transaction.objectStore(METADATA_STORE).put(offlineMetadata);
  });
}

/**
 * Get an offline audio file by ID
 */
export async function getFile(fileId: string): Promise<OfflineFile | null> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(AUDIO_STORE, 'readonly');
    const request = transaction.objectStore(AUDIO_STORE).get(fileId);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result || null);
  });
}

/**
 * Check if a file is available offline
 */
export async function hasFile(fileId: string): Promise<boolean> {
  const file = await getFile(fileId);
  return file !== null;
}

/**
 * Delete an offline file
 */
export async function deleteFile(fileId: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([AUDIO_STORE, METADATA_STORE], 'readwrite');
    transaction.onerror = () => reject(transaction.error);
    transaction.oncomplete = () => resolve();

    transaction.objectStore(AUDIO_STORE).delete(fileId);
    transaction.objectStore(METADATA_STORE).delete(fileId);
  });
}

/**
 * List all offline files with metadata
 */
export async function listFiles(): Promise<OfflineMetadata[]> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(METADATA_STORE, 'readonly');
    const request = transaction.objectStore(METADATA_STORE).getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result || []);
  });
}

/**
 * Get all offline files for a specific song
 */
export async function getFilesForSong(songId: string): Promise<OfflineMetadata[]> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(METADATA_STORE, 'readonly');
    const index = transaction.objectStore(METADATA_STORE).index('songId');
    const request = index.getAll(songId);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result || []);
  });
}

/**
 * Delete all offline files for a song
 */
export async function deleteFilesForSong(songId: string): Promise<void> {
  const files = await getFilesForSong(songId);

  for (const file of files) {
    await deleteFile(file.fileId);
  }
}

/**
 * Get storage usage information
 */
export async function getStorageUsage(): Promise<StorageUsage> {
  // Try to get storage estimate if available
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    try {
      const estimate = await navigator.storage.estimate();
      return {
        used: estimate.usage || 0,
        total: estimate.quota || 0,
        percentage: estimate.quota ? ((estimate.usage || 0) / estimate.quota) * 100 : 0,
      };
    } catch {
      // Fall back to calculating from stored files
    }
  }

  // Calculate from stored files
  const files = await listAllFiles();
  const used = files.reduce((acc, file) => acc + file.size, 0);

  return {
    used,
    total: 0, // Unknown
    percentage: 0,
  };
}

/**
 * List all offline files with full data (including blob size)
 */
async function listAllFiles(): Promise<OfflineFile[]> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(AUDIO_STORE, 'readonly');
    const request = transaction.objectStore(AUDIO_STORE).getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result || []);
  });
}

/**
 * Clear all offline storage
 */
export async function clearAllStorage(): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([AUDIO_STORE, METADATA_STORE, PEAKS_STORE], 'readwrite');
    transaction.onerror = () => reject(transaction.error);
    transaction.oncomplete = () => resolve();

    transaction.objectStore(AUDIO_STORE).clear();
    transaction.objectStore(METADATA_STORE).clear();
    transaction.objectStore(PEAKS_STORE).clear();
  });
}

/**
 * Create an object URL for offline playback
 */
export async function getOfflineAudioUrl(fileId: string): Promise<string | null> {
  const file = await getFile(fileId);
  if (!file) return null;

  return URL.createObjectURL(file.blob);
}

/**
 * Save waveform peaks data for a file
 */
export async function savePeaks(
  fileId: string,
  peaks: number[][],
  duration: number,
  sampleRate: number = 44100
): Promise<void> {
  const db = await openDB();

  const peaksData: WaveformPeaks = {
    fileId,
    peaks,
    duration,
    sampleRate,
    createdAt: Date.now(),
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(PEAKS_STORE, 'readwrite');
    transaction.onerror = () => reject(transaction.error);
    transaction.oncomplete = () => resolve();

    transaction.objectStore(PEAKS_STORE).put(peaksData);
  });
}

/**
 * Get cached waveform peaks for a file
 */
export async function getPeaks(fileId: string): Promise<WaveformPeaks | null> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(PEAKS_STORE, 'readonly');
    const request = transaction.objectStore(PEAKS_STORE).get(fileId);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result || null);
  });
}

/**
 * Check if peaks are cached for a file
 */
export async function hasPeaks(fileId: string): Promise<boolean> {
  const peaks = await getPeaks(fileId);
  return peaks !== null;
}

/**
 * Delete cached peaks for a file
 */
export async function deletePeaks(fileId: string): Promise<void> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(PEAKS_STORE, 'readwrite');
    transaction.onerror = () => reject(transaction.error);
    transaction.oncomplete = () => resolve();

    transaction.objectStore(PEAKS_STORE).delete(fileId);
  });
}

/**
 * Format bytes to human readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}
